import { NgModule } from "@angular/core";
import { FLOW_ID } from "../enum/tracking.enum";
import { CMSConfig } from "../model/cms-model";
import { CMSConfigModel } from "../model/cmsConfigs.model";
import { Parameters } from "../model/parameters";
import { ProposalResponse } from "../model/proposal-response";

interface Storage {
    readonly length: number;
    clear(): void;
    getItem(key: string): string | null;
    key(index: number): string | null;
    removeItem(key: string): void;
    setItem(key: string, data: string): void;
    [key: string]: any;
    [index: number]: string;
}
type ErrorMessageTypes = {
    default?: string;
    tokenError?: string;
    isLoaded?: string;
};
@NgModule()
export class StorageModule {
    readonly PREFIX = "stdr#";
    readonly ERROR = "Error";
    readonly SUGGESTION = "suggestion";
    readonly KEY_PROPOSAL = "proposal";
    readonly KEY_SELECTED_OFFER = "selectedOffer";
    readonly PARAMETERS = "parameters";
    readonly AUTH_KEY = "auth_key";
    readonly IMAGES_STORE = "IMAGES";
    readonly THUMBS_STORE = "THUMBS";
    readonly KEY_PAGES = "pages";
    readonly KEY_THEME = "theme";
    readonly KEY_DRAFT = "draft";
    readonly KEY_EXTERNAL_IP = "external_ip";
    readonly KEY_RESUME_PAGE_ONLINE = "resume_page_online";
    readonly KEY_MONOTYPE = "flowId";
    readonly KEY_FLOW_NAME = "flowName";
    readonly KEY_RECOVERY = "recovery";
    readonly KEY_CAPTURE = "capture";
    readonly KEY_DOCUMENT = "documento";
    readonly KEY_GAID = 'gaId';
    readonly KEY_PROP_HASH_ID = 'propHashId';
    readonly KEY_CURENT_STEP = "current";
    readonly KEY_USER_CAN_CAPTURE = "userCanCapture";
    readonly KEY_ERROR_CAN_CAPTURE = "errorCanCapture";
    readonly KEY_ERROR_CAN_OFF_CAPTURE = "errorCanOffCapture";
    readonly KEY_IS_RESET = "isReset";
    readonly KEY_CAN_RESET = "canReset";
    readonly KEY_IS_RESUME = "isResume";
    readonly KEY_RESUME_STEP = "resumeStep";
    readonly KEY_ADSENSE = "adsense";
    readonly KEY_ERRO = "error";
    readonly KEY_DYNATRACE_STATUS = "dynatraceStatus";
    readonly KEY_CONFIGS = "configs";
    readonly DIMENSION_KEY = "dimensionKey";
    readonly KEY_IMAGE = "doc_image";
    readonly KEY_FB_LIST = "fbList";
    readonly KEY_CMS_PAGES = "cmsPages";
    readonly KEY_ERROR_PAGES = "errorPages";
    readonly PAGE_VIEW_PRE_ERROR = 'pageViewPreEror';
    readonly KEY_LANDING_ORIGIN = 'landing-origin';
    readonly KEY_LANDING_PARAMETERS = 'parameters';


    public storage: Storage;

    constructor() {
        this.storage = sessionStorage;
    }

    appKey(key: string): string {
        return this.PREFIX + key;
    }

    /**
     * Adiciona um elemento no storage.
     * @param key chave
     * @param value valor a adicionar
     */
    put(key: string, value: any) {
        this.storage.setItem(this.appKey(key), value);
    }

    /**
     * Obtem um valor do storage.
     * @param key chave
     * @return o valor ou null caso nao exista.
     */
    get(key: string, _default: any = ""): string {
        let keyValue = this.storage.getItem(this.appKey(key));
        return keyValue ? keyValue : _default;
    }

    /**
     * Remove um valor do storage.
     * @param key chave
     * @return o valor ou null caso nao exista.
     */
    remove(key: string): string {
        const value = this.get(key);
        this.storage.removeItem(this.appKey(key));
        return value;
    }

    clear() {
        this.storage.clear();
    }

    getParameters(): Parameters {
        return JSON.parse(this.get(this.PARAMETERS));
    }

    setProposal(proposal: ProposalResponse) {
        this.put(this.KEY_PROPOSAL, JSON.stringify(proposal));
    }

    setPageView(pageview: string) {
        this.put("pageview", pageview);
    }

    getPageView(): string {
        return this.get("pageview", null);
    }

    updateProposal(proposal: ProposalResponse) {
        const currentProposal = this.getProposal();
        this.put(
            this.KEY_PROPOSAL,
            JSON.stringify({ ...currentProposal, ...proposal })
        );
    }

    getProposal(): ProposalResponse {
        return JSON.parse(this.get(this.KEY_PROPOSAL, "{}"));
    }

    hasProposal() {
        return this.get(this.KEY_PROPOSAL, "").trim().length > 0;
    }

    setDataErrorCode(error: any) {
        this.put(this.ERROR, JSON.stringify(error));
    }

    getDataErrorCode(): any {
        return JSON.parse(
            this.hasDataErrorCode() ? this.get(this.ERROR, "{}") : "{}"
        );
    }

    hasDataErrorCode() {
        return (
            this.get(this.ERROR, null) !== null ||
            this.get(this.ERROR, null) !== undefined
        );
    }

    setSuggestionEmail(suggestion: any) {
        this.put(this.SUGGESTION, JSON.stringify(suggestion));
    }

    getSuggestionEmail(): any {
        return JSON.parse(this.get(this.SUGGESTION));
    }

    setAuthKey(key): void {
        this.put(this.AUTH_KEY, key);
    }

    getAuthKey(): string {
        return this.get(this.AUTH_KEY);
    }

    setPages(pages: any) {
        this.put(this.KEY_PAGES, JSON.stringify(pages));
    }

    getPages(): any {
        return JSON.parse(this.get(this.KEY_PAGES));
    }

    getTheme() {
        return JSON.parse(this.get(this.KEY_THEME));
    }

    setTheme(theme: any) {
        this.put(this.KEY_THEME, JSON.stringify(theme));
    }

    setDraft(name: string, value: any) {
        let draftObj = this.getDraft(name);

        Object.defineProperty(draftObj, name, {
            value,
            enumerable: true,
        });

        this.put(this.KEY_DRAFT, JSON.stringify(draftObj));
    }

    getDraft(name: string) {
        const draftObj = JSON.parse(this.get(this.KEY_DRAFT));
        return draftObj ? draftObj[name] : {};
    }

    setExternalIP(ip_address: string) {
        this.put(this.KEY_EXTERNAL_IP, ip_address);
    }

    getExternalIP(): string {
        return this.get(this.KEY_EXTERNAL_IP);
    }

    setResumeOnlinePage(page_url: string) {
        this.put(this.KEY_RESUME_PAGE_ONLINE, JSON.stringify(page_url));
    }

    getResumeOnlinePage(): string {
        return JSON.parse(this.get(this.KEY_RESUME_PAGE_ONLINE));
    }

    hasMonoType() {
        return (
            this.getFlowId() !== "null" && this.getFlowId().trim().length > 0
        );
    }

    getCapture(): string {
        return this.get(this.KEY_CAPTURE, "null");
    }

    setCapture(capture: string) {
        this.put(this.KEY_CAPTURE, capture);
    }

    getRecovery(): string {
        return this.get(this.KEY_RECOVERY, "null");
    }

    setRecovery(monoType: string) {
        this.put(this.KEY_RECOVERY, monoType);
    }

    setFlowId(monoType: string) {
        this.put(this.KEY_MONOTYPE, monoType);
    }

    getFlowId(): string {
        return this.get(this.KEY_MONOTYPE, "");
    }

    setDocument(number: string) {
        this.put(this.KEY_DOCUMENT, number);
    }

    getDocument(): string {
        return this.get(this.KEY_DOCUMENT, '');
    }

    setGaId(number: string) {
        this.put(this.KEY_GAID, number);
    }

    getGaId(): string {
        return this.get(this.KEY_GAID, '');
    }

    setPropHashId(number: string) {
        this.put(this.KEY_PROP_HASH_ID, number);
    }

    getPropHashId(): string {
        return this.get(this.KEY_PROP_HASH_ID, '');
    }

    setFlowName(name: string) {
        this.put(this.KEY_FLOW_NAME, name);
    }

    getFlowName(): string {
        return this.get(
            this.KEY_FLOW_NAME,
            FLOW_ID[this.get(this.KEY_MONOTYPE)]
        );
    }

    getIsMono() {
        return this.getFlowId().trim().length > 0 ||
            this.getFlowName().trim().length > 0
            ? this.getFlowId().trim() === "10" ||
            this.getFlowName().trim() === "MONO"
            : false;
    }

    get document(): string {
        return this.get(this.KEY_DOCUMENT, "");
    }

    hasDocument() {
        return this.document.trim().length > 0;
    }

    setCurrentStepData(data: ProposalResponse) {
        this.put(this.KEY_CURENT_STEP, JSON.stringify(data));
    }

    getCurrentStepData(): ProposalResponse {
        return JSON.parse(this.get(this.KEY_CURENT_STEP));
    }

    hasCurrentStepData() {
        return this.get(this.KEY_CURENT_STEP) !== null;
    }

    setUserCanCapture(value: boolean) {
        this.put(this.KEY_USER_CAN_CAPTURE, JSON.stringify(value));
    }

    getUserCanCapture(): boolean {
        return JSON.parse(
            this.get(this.KEY_USER_CAN_CAPTURE) === null
                ? "false"
                : this.get(this.KEY_USER_CAN_CAPTURE)
        );
    }

    setCanReset(value: boolean) {
        this.put(this.KEY_CAN_RESET, JSON.stringify(value));
    }

    getCanReset(): boolean {
        return JSON.parse(
            this.get(this.KEY_CAN_RESET) === null
                ? "false"
                : this.get(this.KEY_CAN_RESET)
        );
    }

    setIsReset(value: boolean) {
        this.put(this.KEY_IS_RESET, JSON.stringify(value));
    }

    getIsReset(): boolean {
        return JSON.parse(
            this.get(this.KEY_IS_RESET, "null") === "null"
                ? "false"
                : this.get(this.KEY_IS_RESET)
        );
    }

    setIsResume(value: boolean) {
        this.put(this.KEY_IS_RESUME, JSON.stringify(value));
    }

    getIsResume() {
        return JSON.parse(
            this.get(this.KEY_IS_RESUME, "null") === "null"
                ? "false"
                : this.get(this.KEY_IS_RESUME)
        );
    }

    setResumeStep(value: string) {
        this.put(this.KEY_RESUME_STEP, JSON.stringify(value));
    }

    getResumeStep() {
        return JSON.parse(
            this.get(this.KEY_RESUME_STEP, "null") === "null"
                ? "false"
                : this.get(this.KEY_RESUME_STEP)
        );
    }

    setErro(value: any) {
        this.put(this.ERROR, JSON.stringify(value));
    }

    getError() {
        return JSON.parse(
            this.get(this.ERROR, "null") === "null"
                ? "false"
                : this.get(this.ERROR)
        );
    }

    getAdsense() {
        return JSON.parse(this.get(this.KEY_ADSENSE, "[]"));
    }

    setDynatraceStatus(value: boolean) {
        this.put(this.KEY_DYNATRACE_STATUS, JSON.stringify(value));
    }

    getDynatraceStatus(): boolean {
        return JSON.parse(
            this.get(this.KEY_DYNATRACE_STATUS, "null") === "null"
                ? "false"
                : this.get(this.KEY_DYNATRACE_STATUS)
        );
    }

    setConfigs(value) {
        this.put(this.KEY_CONFIGS, JSON.stringify(value));
    }

    getConfigs(): any {
        return JSON.parse(
            this.get(this.KEY_CONFIGS, "null") === "null"
                ? "{}"
                : this.get(this.KEY_CONFIGS)
        );
    }

    setDimesions(value) {
        this.put(this.DIMENSION_KEY, JSON.stringify(value));
    }

    getDimensions() {
        return JSON.parse(
            this.get(this.DIMENSION_KEY, "null") === "null"
                ? "false"
                : this.get(this.DIMENSION_KEY)
        );
    }

    setImage(id, value): void {
        const key = `${this.IMAGES_STORE}#${id}`;
        this.put(key, value);
    }

    getImage(id): string {
        const key = `${this.IMAGES_STORE}#${id}`;
        return this.get(key);
    }

    setThumbnail(id, value): void {
        const key = `${this.THUMBS_STORE}#${id}`;
        this.put(key, value);
    }

    getThumbnail(id): string {
        const key = `${this.THUMBS_STORE}#${id}`;
        return this.get(key);
    }

    resetImages(): void {
        Object.keys(sessionStorage)
            .filter(
                (key) =>
                    key.includes(this.IMAGES_STORE) ||
                    key.includes(this.THUMBS_STORE)
            )
            .forEach((key) => this.storage.removeItem(key));
    }

    setCmsPages(value: string) {
        this.put(this.KEY_CMS_PAGES, value);
    }

    getCmsPages(): string {
        return this.get(this.KEY_CMS_PAGES, "null") === "null"
            ? "false"
            : this.get(this.KEY_CMS_PAGES);
    }

    setErrorPages(value: string) {
        this.put(this.KEY_ERROR_PAGES, value);
    }

    getErrorPages(): string {
        return this.get(this.KEY_ERROR_PAGES, "null") === "null"
            ? "false"
            : this.get(this.KEY_ERROR_PAGES);
    }

    setErrorCanCapture(value: boolean) {
        this.put(this.KEY_ERROR_CAN_CAPTURE, JSON.stringify(value));
    }

    getErrorCanCapture(): boolean {
        return JSON.parse(
            !this.get(this.KEY_ERROR_CAN_CAPTURE)
                ? "false"
                : this.get(this.KEY_ERROR_CAN_CAPTURE)
        );
    }

    setErrorCanOffCapture(value: boolean) {
        this.put(this.KEY_ERROR_CAN_OFF_CAPTURE, JSON.stringify(value));
    }

    getErrorCanOffCapture(): boolean {
        return JSON.parse(
            !this.get(this.KEY_ERROR_CAN_OFF_CAPTURE)
                ? "false"
                : this.get(this.KEY_ERROR_CAN_OFF_CAPTURE)
        );
    }
    setPageViewList(pageviewList) {
        this.put(this.KEY_FB_LIST, JSON.stringify(pageviewList));
    }

    getPageViewList() {
        return JSON.parse(
            !this.get(this.KEY_FB_LIST) ? "false" : this.get(this.KEY_FB_LIST)
        );
    }

    resetPageviewList() {
        // sessionStorage.removeItem('stdr#fbList');
    }

      /**
   * Landing Page URL stored on Landing Page access by user
   */
    get landingParameters(): string {
        return this.get(this.KEY_LANDING_PARAMETERS, '' );
    }
}
